import { Document, Page, Text, View, StyleSheet, Image, Link } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

const CLOUDINARY_BASE_URL = 'https://res.cloudinary.com/zenbusiness/q_auto/v1/shared-assets/s2';
const ZENBUSINESS_LOGO_URL =
  'https://res.cloudinary.com/zenbusiness/image/upload/v1749845000/shared-assets/s2/logo/zb-velo-compact-color.png';

const FOOTER_HEIGHT = 40;

const styles = StyleSheet.create({
  page: {
    padding: 32,
    paddingBottom: 32 + FOOTER_HEIGHT,
    fontSize: 12,
    lineHeight: 1.5,
    color: '#111'
  },
  header: {
    marginBottom: 24,
    borderBottom: '1pt solid #ccc',
    paddingBottom: 12
  },
  subheader: {
    fontSize: 10,
    color: 'grey',
    textTransform: 'uppercase',
    marginBottom: 8
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8
  },
  generatedDate: {
    fontSize: 9,
    color: '#999',
    marginTop: 4
  },
  businessInfo: {
    marginTop: 12,
    padding: 12,
    backgroundColor: '#f5f5f5',
    borderRadius: 4
  },
  businessInfoRow: {
    flexDirection: 'row',
    marginBottom: 6
  },
  businessInfoLabel: {
    fontSize: 10,
    color: '#666',
    width: 120
  },
  businessInfoValue: {
    fontSize: 10,
    color: '#333',
    fontWeight: 'bold',
    flex: 1
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 12,
    color: '#222'
  },
  sloganCard: {
    marginBottom: 12,
    padding: 12,
    backgroundColor: '#fafafa',
    borderRadius: 4,
    borderLeft: '3pt solid #3971E7'
  },
  sloganText: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#111'
  },
  sloganInsight: {
    fontSize: 10,
    color: '#666',
    marginBottom: 6
  },
  sloganMeta: {
    fontSize: 9,
    color: '#888',
    fontWeight: 'bold'
  },
  footer: {
    position: 'absolute',
    left: 32,
    right: 32,
    bottom: 24,
    height: FOOTER_HEIGHT,
    backgroundColor: '#fff'
  },
  footerRule: {
    borderTop: '1pt solid #ddd',
    marginBottom: 6
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  footerLeft: {
    fontSize: 10,
    color: '#666'
  },
  footerRightImg: {
    width: 180,
    height: 15
  }
});

/**
 * PDF document component for Slogan Generator results
 * Generates a downloadable PDF with selected slogans and business context
 *
 * @param {object} props - Component props
 * @param {string} [props.environment] - Deployment environment for links
 * @param {Array} props.slogans - Array of slogan objects to include in PDF
 * @param {object} [props.userInput] - User's business information
 * @component
 */
export function SloganGeneratorPDF({ environment, slogans, userInput }) {
  const generatedDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const websiteUrl = environment
    ? `https://www.zenbusiness.com${environment !== 'production' ? `-${environment}` : ''}`
    : 'https://www.zenbusiness.com';

  // Build business info rows
  const businessRows = [];
  if (userInput?.businessName) {
    businessRows.push({ label: 'Business Name', value: userInput.businessName });
  }
  if (userInput?.industry) {
    businessRows.push({ label: 'Industry', value: userInput.industry });
  }
  if (userInput?.location) {
    businessRows.push({ label: 'Location', value: userInput.location });
  }
  if (userInput?.description) {
    businessRows.push({ label: 'Description', value: userInput.description });
  }
  if (userInput?.uniqueSellingPoint) {
    businessRows.push({ label: 'Unique Selling Point', value: userInput.uniqueSellingPoint });
  }
  if (userInput?.brandPersonality) {
    businessRows.push({ label: 'Brand Personality', value: userInput.brandPersonality });
  }
  if (userInput?.preferredStyle) {
    businessRows.push({ label: 'Preferred Style', value: userInput.preferredStyle });
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image src={ZENBUSINESS_LOGO_URL} style={{ width: 80, marginBottom: 20 }} />

        <View style={styles.header}>
          <Text style={styles.subheader}>Business Slogan Generator</Text>
          <Text style={styles.title}>
            {userInput?.businessName
              ? `Custom Slogans for ${userInput.businessName}`
              : 'Your Custom Slogans'}
          </Text>
          <Text style={styles.generatedDate}>Generated on {generatedDate}</Text>

          {businessRows.length > 0 && (
            <View style={styles.businessInfo}>
              {businessRows.map((row) => (
                <View key={row.label} style={styles.businessInfoRow}>
                  <Text style={styles.businessInfoLabel}>{row.label}:</Text>
                  <Text style={styles.businessInfoValue}>{row.value}</Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Slogans */}
        <Text style={styles.sectionTitle}>
          {slogans.length === 1 ? '1 Slogan' : `${slogans.length} Slogans`}
        </Text>

        {slogans.map((slogan, index) => (
          <View key={slogan.id || index} style={styles.sloganCard} wrap={false}>
            <Text style={styles.sloganText}>&ldquo;{slogan.text}&rdquo;</Text>
            {slogan.insight && <Text style={styles.sloganInsight}>{slogan.insight}</Text>}
            {(slogan.category || slogan.tone) && (
              <Text style={styles.sloganMeta}>
                {[
                  slogan.category && `Category: ${slogan.category}`,
                  slogan.tone && `Tone: ${slogan.tone}`
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Text>
            )}
          </View>
        ))}

        <View fixed style={styles.footer}>
          <View style={styles.footerRule} />
          <View style={styles.footerRow}>
            <Link href={websiteUrl} style={styles.footerLeft}>
              support@zenbusiness.com
            </Link>
            <Image
              src={`${CLOUDINARY_BASE_URL}/raster/powered-by-zb-velo.png`}
              style={styles.footerRightImg}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
}

SloganGeneratorPDF.propTypes = {
  environment: PropTypes.string,
  slogans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string.isRequired,
      category: PropTypes.string,
      tone: PropTypes.string,
      insight: PropTypes.string
    })
  ).isRequired,
  userInput: PropTypes.shape({
    businessName: PropTypes.string,
    industry: PropTypes.string,
    location: PropTypes.string,
    description: PropTypes.string,
    uniqueSellingPoint: PropTypes.string,
    brandPersonality: PropTypes.string,
    preferredStyle: PropTypes.string
  })
};

export default SloganGeneratorPDF;
